import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Trash2, ChevronDown, ChevronUp, AlertTriangle, ShieldCheck, AlertOctagon } from "lucide-react";
import { toast } from "sonner";

import OverviewTab from "../components/deal/OverviewTab";
import TimelineTab from "../components/deal/TimelineTab";
import DocumentsTab from "../components/deal/DocumentsTab";
import BuyersTab from "../components/deal/BuyersTab";
import IOIsTab from "../components/deal/IOIsTab";
import NotesTab from "../components/deal/NotesTab";
import AITab from "../components/deal/AITab";
import NormalisationTab from "../components/deal/NormalisationTab";
import FeesTab from "../components/deal/FeesTab";
import { ReadinessTab } from "../components/deal/ReadinessTab";
import { FeasibilityTab } from "../components/deal/FeasibilityTab";
import WiTab from "../components/deal/WiTab";
import SellerPortalTab from "../components/deal/SellerPortalTab";
import DueDiligenceTab from "../components/deal/DueDiligenceTab";
import OffersTab from "../components/deal/OffersTab";
import ConditionsTab from "../components/deal/ConditionsTab";

const RISK_CFG = {
    Low:    { cls: "bg-green-50 text-green-700 border-green-200",  dot: "bg-green-500",  Icon: ShieldCheck },
    Medium: { cls: "bg-amber-50 text-amber-700 border-amber-200",  dot: "bg-amber-400",  Icon: AlertTriangle },
    High:   { cls: "bg-red-50 text-red-700 border-red-200",        dot: "bg-red-500",    Icon: AlertOctagon },
};

function RiskPanel({ dealId, stage }) {
    const [risk, setRisk]       = useState(null);
    const [open, setOpen]       = useState(false);
    const [loading, setLoading] = useState(false);
    const TERMINAL = ["Completed", "Lost"];

    useEffect(() => {
        if (TERMINAL.includes(stage)) return;
        setLoading(true);
        api.get(`/deals/${dealId}/risk-score`)
            .then((r) => setRisk(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dealId, stage]);

    if (TERMINAL.includes(stage) || (!loading && !risk)) return null;
    if (loading) return <div className="text-xs text-muted-foreground px-1">Computing risk…</div>;

    const band = risk.risk_band || "Low";
    const cfg  = RISK_CFG[band] || RISK_CFG.Low;
    const { Icon } = cfg;

    return (
        <div className="border border-border rounded-md overflow-hidden" data-testid="risk-panel">
            <button
                onClick={() => setOpen((o) => !o)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left ${cfg.cls} border-b border-inherit hover:opacity-90 transition-opacity`}
                data-testid="risk-panel-toggle"
            >
                <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="text-xs font-semibold uppercase tracking-wider">
                        Risk: {band}
                    </span>
                    <span className="font-mono text-xs opacity-70">({risk.risk_score}/100)</span>
                </div>
                {open ? <ChevronUp className="w-3.5 h-3.5 opacity-60" /> : <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
            </button>

            {open && (
                <div className="bg-white px-4 py-3 space-y-3">
                    {risk.signals?.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Driving signals</div>
                            <div className="space-y-1.5">
                                {risk.signals.map((s, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs" data-testid={`risk-signal-${s.dimension}`}>
                                        <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${cfg.dot}`} />
                                        <span className="text-muted-foreground">{s.detail}</span>
                                        <span className="ml-auto font-mono text-[10px] text-muted-foreground flex-shrink-0">+{s.points}pts</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {risk.recommended_actions?.length > 0 && (
                        <div className="border-t border-border pt-2.5">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Recommended actions</div>
                            <ol className="list-decimal list-inside space-y-1">
                                {risk.recommended_actions.map((a, i) => (
                                    <li key={i} className="text-xs text-foreground/80" data-testid={`risk-action-${i}`}>{a}</li>
                                ))}
                            </ol>
                        </div>
                    )}
                    {risk.signals?.length === 0 && (
                        <p className="text-xs text-green-700">No active risk signals — deal momentum is healthy.</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default function DealDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [deal, setDeal] = useState(null);
    const [stages, setStages] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        const [d, s] = await Promise.all([api.get(`/deals/${id}`), api.get(`/stages`)]);
        setDeal(d.data);
        setStages(s.data);
    };

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, [id]);

    const updateDeal = async (patch) => {
        const { data } = await api.patch(`/deals/${id}`, patch);
        setDeal(data);
    };

    const deleteDeal = async () => {
        if (!confirm("Delete this deal permanently?")) return;
        await api.delete(`/deals/${id}`);
        toast.success("Deal deleted");
        nav("/pipeline");
    };

    if (loading || !deal) return <div className="p-8 text-sm text-muted-foreground">Loading deal…</div>;

    return (
        <div data-testid="deal-detail-page">
            <PageHeader title={deal.company_name} subtitle={deal.sector ? `${deal.sector} · ${deal.location || "No location"}` : (deal.location || "—")}>
                <Link to="/pipeline" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-to-pipeline-link">
                    <ArrowLeft className="w-3.5 h-3.5" /> Pipeline
                </Link>
                <Select value={deal.stage} onValueChange={(v) => updateDeal({ stage: v })}>
                    <SelectTrigger className="w-44" data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {stages.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={deleteDeal} data-testid="delete-deal-btn">
                    <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
            </PageHeader>

            <div className="p-6 lg:p-8">
                {/* Summary row */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                    <Stat label="Deal Size" value={fmtMoney(deal.deal_size, deal.currency)} money />
                    <Stat label="Fee" value={deal.fee ? fmtMoney(deal.fee, deal.currency) : "—"} money />
                    <Stat label="Probability" value={`${deal.probability}%`} />
                    <Stat label="Revenue" value={deal.revenue ? fmtMoney(deal.revenue, deal.currency) : "—"} money />
                    <EbitdaStat deal={deal} />
                    <Stat label="Currency" value={deal.currency} />
                </div>

                {/* Risk panel */}
                <div className="mb-5">
                    <RiskPanel dealId={id} stage={deal.stage} />
                </div>

                <Tabs defaultValue="overview" className="space-y-4">
                    <TabsList className="flex flex-wrap h-auto justify-start" data-testid="deal-tabs">
                        <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                        <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                        <TabsTrigger value="normalisation" data-testid="tab-normalisation">EBITDA</TabsTrigger>
                        <TabsTrigger value="fees" data-testid="tab-fees">Fees</TabsTrigger>
                        <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                        <TabsTrigger value="buyers" data-testid="tab-buyers">Buyers</TabsTrigger>
                        <TabsTrigger value="iois" data-testid="tab-iois">IOIs</TabsTrigger>
                        <TabsTrigger value="offers" data-testid="tab-offers">Offers</TabsTrigger>
                        <TabsTrigger value="cps" data-testid="tab-cps">
                            CPs{deal.cp_total ? ` ${deal.cp_resolved || 0}/${deal.cp_total}` : ""}
                        </TabsTrigger>
                        <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
                        <TabsTrigger value="readiness" data-testid="tab-readiness">Readiness</TabsTrigger>
                        <TabsTrigger value="feasibility" data-testid="tab-feasibility">Feasibility</TabsTrigger>
                        <TabsTrigger value="wi" data-testid="tab-wi">W&amp;I</TabsTrigger>
                        <TabsTrigger value="portal" data-testid="tab-portal">Seller portal</TabsTrigger>
                        <TabsTrigger value="dd" data-testid="tab-dd">Due diligence</TabsTrigger>
                        <TabsTrigger value="ai" data-testid="tab-ai">AI</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview"><OverviewTab deal={deal} onSave={updateDeal} /></TabsContent>
                    <TabsContent value="timeline"><TimelineTab dealId={id} /></TabsContent>
                    <TabsContent value="normalisation"><NormalisationTab deal={deal} onRefresh={refresh} /></TabsContent>
                    <TabsContent value="fees"><FeesTab deal={deal} /></TabsContent>
                    <TabsContent value="documents"><DocumentsTab dealId={id} /></TabsContent>
                    <TabsContent value="buyers"><BuyersTab dealId={id} /></TabsContent>
                    <TabsContent value="iois"><IOIsTab dealId={id} deal={deal} /></TabsContent>
                    <TabsContent value="offers"><OffersTab dealId={id} deal={deal} onSaveDeal={updateDeal} /></TabsContent>
                    <TabsContent value="cps"><ConditionsTab dealId={id} onChange={refresh} /></TabsContent>
                    <TabsContent value="notes"><NotesTab dealId={id} /></TabsContent>
                    <TabsContent value="readiness"><ReadinessTab dealId={id} stage={deal.stage} /></TabsContent>
                    <TabsContent value="feasibility"><FeasibilityTab dealId={id} deal={deal} /></TabsContent>
                    <TabsContent value="wi"><WiTab dealId={id} deal={deal} /></TabsContent>
                    <TabsContent value="portal"><SellerPortalTab dealId={id} /></TabsContent>
                    <TabsContent value="dd"><DueDiligenceTab dealId={id} deal={deal} /></TabsContent>
                    <TabsContent value="ai"><AITab deal={deal} /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function Stat({ label, value, money }) {
    return (
        <div className="border border-[#E2E8F0] rounded-[6px] bg-white py-4 px-4" style={{boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}>
            <div className="text-[11px] uppercase tracking-[0.06em] text-[#64748B] font-medium mb-1.5">{label}</div>
            <div className="font-mono text-xl font-semibold" style={{color: money ? '#C9A227' : '#0B1F3A'}}>{value}</div>
        </div>
    );
}

function EbitdaStat({ deal }) {
    const hasNormalisation = deal.reported_ebitda != null || (deal.addbacks_count || 0) > 0;
    if (!hasNormalisation) {
        return <Stat label="EBITDA" value={deal.ebitda ? fmtMoney(deal.ebitda, deal.currency) : "—"} />;
    }
    const reported = deal.reported_ebitda != null ? fmtMoney(deal.reported_ebitda, deal.currency) : "—";
    const normalised = deal.normalised_ebitda != null ? fmtMoney(deal.normalised_ebitda, deal.currency) : "—";
    return (
        <div className="border border-gold/40 rounded-md bg-white p-4" data-testid="ebitda-stat-normalised">
            <div className="text-[10px] uppercase tracking-wider text-gold mb-1 font-semibold">EBITDA (normalised)</div>
            <div className="font-mono text-lg font-bold text-gold leading-tight">{normalised}</div>
            <div className="font-mono text-[11px] text-muted-foreground mt-0.5">Reported {reported}</div>
        </div>
    );
}
